import { useEffect, useRef, useState, useCallback } from 'react';

const journey = [
  {
    year: 'Age 60',
    label: 'The Wake-Up Call',
    body: '240 lbs. Pre-diabetic. Three medications. Hair falling out. My doctor said I was heading for serious trouble. I couldn\'t walk to the mailbox without getting winded. That was the day I decided enough was enough.',
    stat: '240 lbs',
    statLabel: 'Starting Weight',
  },
  {
    year: 'Age 61',
    label: 'Building My Own System',
    body: 'Every program I tried was built for 25-year-olds — and I kept getting hurt. So I figured out what actually works for a body in its 60s. Slower recovery. Stiff joints. A metabolism that doesn\'t forgive mistakes. I built my own system from scratch.',
    stat: '−18 lbs',
    statLabel: 'First Year Lost',
  },
  {
    year: 'Age 62',
    label: 'The Transformation',
    body: '55 lbs gone. Off all three medications. Blood pressure and blood sugar — both normal. And after 8 months on my hair protocol, my hair grew back. Stronger, healthier, and looking better than I had in 20 years.',
    stat: '185 lbs',
    statLabel: 'Final Weight',
  },
];

// ─── Mobile scroll-locked view ───────────────────────────────────────────────
function MobileJourney() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [rawProgress, setRawProgress] = useState(0);
  const [visible, setVisible] = useState(true);
  const [slideDir, setSlideDir] = useState<'down' | 'up'>('down');
  const sectionRef = useRef<HTMLDivElement>(null);
  const prevIndexRef = useRef(0);
  const animatingRef = useRef(false);
  const pendingIndexRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);

  const triggerTransition = useCallback((newIndex: number, dir: 'down' | 'up') => {
    if (animatingRef.current) {
      pendingIndexRef.current = newIndex;
      return;
    }
    animatingRef.current = true;
    setVisible(false);
    setSlideDir(dir);

    setTimeout(() => {
      setActiveIndex(newIndex);
      prevIndexRef.current = newIndex;
      setVisible(true);
      setTimeout(() => {
        animatingRef.current = false;
        if (pendingIndexRef.current !== null && pendingIndexRef.current !== newIndex) {
          const next = pendingIndexRef.current;
          pendingIndexRef.current = null;
          triggerTransition(next, next > newIndex ? 'down' : 'up');
        } else {
          pendingIndexRef.current = null;
        }
      }, 500);
    }, 200);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (rafRef.current !== null) return;
      rafRef.current = requestAnimationFrame(() => {
        const section = sectionRef.current;
        if (!section) { rafRef.current = null; return; }
        const rect = section.getBoundingClientRect();
        const scrolledIn = -rect.top;
        const totalScrollable = rect.height - window.innerHeight;
        const progress = Math.max(0, Math.min(1, scrolledIn / totalScrollable));
        setRawProgress(progress);

        const stepsCount = journey.length;
        const zone = 1 / stepsCount;
        let newIndex = 0;
        for (let i = stepsCount - 1; i >= 0; i--) {
          if (progress >= i * zone + zone * 0.18) { newIndex = i; break; }
        }
        newIndex = Math.min(stepsCount - 1, newIndex);

        if (newIndex !== prevIndexRef.current) {
          triggerTransition(newIndex, newIndex > prevIndexRef.current ? 'down' : 'up');
        }
        rafRef.current = null;
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [triggerTransition]);

  const active = journey[activeIndex];
  const progressBarWidth = `${rawProgress * 100}%`;

  const contentStyle: React.CSSProperties = {
    opacity: visible ? 1 : 0,
    transform: visible
      ? 'translateY(0px)'
      : slideDir === 'down' ? 'translateY(20px)' : 'translateY(-20px)',
    transition: visible
      ? 'opacity 0.5s ease, transform 0.5s cubic-bezier(0.16,1,0.3,1)'
      : 'opacity 0.18s ease, transform 0.18s ease',
    willChange: 'opacity, transform',
  };

  return (
    <section
      id="story"
      ref={sectionRef}
      style={{ height: `${journey.length * 80}vh` }}
      className="relative bg-[#0d0d0d]"
      aria-label="Mason's transformation journey from age 60 to 62"
    >
      <div className="sticky top-0 h-screen flex flex-col overflow-hidden">
        {/* Progress bar */}
        <div className="w-full h-0.5 bg-white/5 flex-shrink-0">
          <div
            className="h-full bg-[#c8a96e] transition-all duration-150 ease-out"
            style={{ width: progressBarWidth, boxShadow: '0 0 8px 1px rgba(200,169,110,0.5)', willChange: 'width' }}
          />
        </div>

        {/* Ambient glow */}
        <div
          className="absolute left-1/2 top-1/2 w-[500px] h-[500px] rounded-full pointer-events-none -translate-x-1/2 -translate-y-1/2"
          style={{ background: 'radial-gradient(circle, rgba(200,169,110,0.06) 0%, transparent 65%)' }}
        />

        {/* Big faded year */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none overflow-hidden">
          <span
            className="font-black leading-none"
            style={{ fontSize: 'clamp(120px, 45vw, 260px)', color: 'rgba(200,169,110,0.03)' }}
          >
            {60 + activeIndex}
          </span>
        </div>

        <div className="flex-1 flex flex-col justify-center px-5 relative z-10">
          {/* Section label */}
          <div className="flex items-center gap-3 mb-7">
            <div className="w-8 h-px bg-[#c8a96e]" />
            <span className="text-[#c8a96e] text-[10px] font-black tracking-[0.3em] uppercase">The Journey</span>
          </div>

          {/* Card */}
          <div
            className="relative overflow-hidden rounded-sm border border-[#c8a96e]/20"
            style={{ background: 'rgba(200,169,110,0.04)' }}
          >
            {/* Accent left bar */}
            <div
              className="absolute left-0 top-0 bottom-0 w-[3px] rounded-r-full"
              style={{ background: 'linear-gradient(to bottom, transparent, #c8a96e, transparent)' }}
            />

            <div className="p-6" style={contentStyle}>
              {/* Year badge — NO icon */}
              <div className="mb-5">
                <span className="text-[#c8a96e] text-xs font-black tracking-[0.25em] uppercase bg-[#c8a96e]/10 border border-[#c8a96e]/25 px-3 py-2 rounded-sm">
                  {active.year}
                </span>
              </div>

              {/* Headline */}
              <h2 className="text-2xl font-black text-white leading-tight mb-4 tracking-tight">
                {active.label}
              </h2>

              {/* Body */}
              <p className="text-gray-300 text-sm leading-[1.85] mb-7">
                {active.body}
              </p>

              {/* Stat */}
              <div className="flex items-end gap-4 border-l-2 border-[#c8a96e] pl-5">
                <span className="text-3xl font-black text-[#c8a96e] leading-none">{active.stat}</span>
                <span className="text-gray-500 text-xs uppercase tracking-widest mb-1">{active.statLabel}</span>
              </div>
            </div>
          </div>

          {/* Dots */}
          <div className="flex items-center gap-2 mt-6">
            {journey.map((_, i) => (
              <div
                key={i}
                className="rounded-full transition-all duration-500"
                style={{
                  width: i === activeIndex ? '28px' : '8px',
                  height: '8px',
                  background: i === activeIndex
                    ? '#c8a96e'
                    : i < activeIndex
                    ? 'rgba(200,169,110,0.4)'
                    : 'rgba(255,255,255,0.1)',
                  boxShadow: i === activeIndex ? '0 0 10px 3px rgba(200,169,110,0.5)' : 'none',
                }}
              />
            ))}
            <span className="text-gray-600 text-xs font-bold tracking-widest ml-2">
              {activeIndex + 1} / {journey.length}
            </span>
          </div>
        </div>

        {/* Bottom scroll indicator */}
        <div className="flex-shrink-0 flex flex-col items-center gap-2 pb-6 pt-2">
          <span className="text-gray-600 text-[10px] tracking-[0.3em] uppercase">Keep Scrolling</span>
          <div style={{ animation: 'bounceHint 1.8s ease-in-out infinite' }}>
            <div className="w-5 h-8 rounded-full border border-white/15 flex items-start justify-center pt-1.5">
              <div className="w-1 h-2 rounded-full bg-[#c8a96e]" style={{ animation: 'scrollDot 1.8s ease-in-out infinite' }} />
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes bounceHint {
          0%, 100% { transform: translateY(0); }
          50%       { transform: translateY(5px); }
        }
        @keyframes scrollDot {
          0%   { transform: translateY(0);    opacity: 1; }
          75%  { transform: translateY(10px); opacity: 0; }
          100% { transform: translateY(0);    opacity: 0; }
        }
      `}</style>
    </section>
  );
}

// ─── Desktop scroll view ─────────────────────────────────────────────────────
function DesktopJourney() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [rawProgress, setRawProgress] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);
  const prevIndexRef = useRef(0);
  const [slideDir, setSlideDir] = useState<'down' | 'up'>('down');
  const [visible, setVisible] = useState(true);
  const animatingRef = useRef(false);
  const pendingIndexRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);

  const triggerTransition = useCallback((newIndex: number, dir: 'down' | 'up') => {
    if (animatingRef.current) {
      pendingIndexRef.current = newIndex;
      return;
    }
    animatingRef.current = true;
    setVisible(false);
    setSlideDir(dir);

    setTimeout(() => {
      setActiveIndex(newIndex);
      prevIndexRef.current = newIndex;
      setVisible(true);
      setTimeout(() => {
        animatingRef.current = false;
        if (pendingIndexRef.current !== null && pendingIndexRef.current !== newIndex) {
          const next = pendingIndexRef.current;
          pendingIndexRef.current = null;
          triggerTransition(next, next > newIndex ? 'down' : 'up');
        } else {
          pendingIndexRef.current = null;
        }
      }, 500);
    }, 200);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (rafRef.current !== null) return;
      rafRef.current = requestAnimationFrame(() => {
        const section = sectionRef.current;
        if (!section) { rafRef.current = null; return; }
        const rect = section.getBoundingClientRect();
        const scrolledIn = -rect.top;
        const totalScrollable = rect.height - window.innerHeight;
        const progress = Math.max(0, Math.min(1, scrolledIn / totalScrollable));
        setRawProgress(progress);

        const stepsCount = journey.length;
        const zone = 1 / stepsCount;
        let newIndex = 0;
        for (let i = stepsCount - 1; i >= 0; i--) {
          if (progress >= i * zone + zone * 0.18) { newIndex = i; break; }
        }
        newIndex = Math.min(stepsCount - 1, newIndex);

        if (newIndex !== prevIndexRef.current) {
          triggerTransition(newIndex, newIndex > prevIndexRef.current ? 'down' : 'up');
        }
        rafRef.current = null;
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [triggerTransition]);

  const active = journey[activeIndex];

  const contentStyle: React.CSSProperties = {
    opacity: visible ? 1 : 0,
    transform: visible
      ? 'translateY(0px)'
      : slideDir === 'down' ? 'translateY(24px)' : 'translateY(-24px)',
    transition: visible
      ? 'opacity 0.5s ease, transform 0.5s cubic-bezier(0.16,1,0.3,1)'
      : 'opacity 0.15s ease, transform 0.15s ease',
    willChange: 'opacity, transform',
  };

  const progressBarWidth = `${rawProgress * 100}%`;
  const showScrollHint = rawProgress < 0.05;

  return (
    <section
      id="story"
      ref={sectionRef}
      style={{ height: `${journey.length * 80}vh` }}
      className="relative bg-[#0d0d0d]"
      aria-label="Mason's transformation journey from age 60 to 62"
    >
      <div className="sticky top-0 h-screen flex flex-col overflow-hidden">
        {/* Progress bar */}
        <div className="w-full h-0.5 bg-white/5 flex-shrink-0">
          <div
            className="h-full bg-[#c8a96e] transition-all duration-150 ease-out"
            style={{ width: progressBarWidth, boxShadow: '0 0 8px 1px rgba(200,169,110,0.5)', willChange: 'width' }}
          />
        </div>

        {/* Ambient glow */}
        <div
          className="absolute left-[-80px] top-1/2 w-[600px] h-[600px] rounded-full pointer-events-none"
          style={{
            background: 'radial-gradient(circle, rgba(200,169,110,0.05) 0%, transparent 65%)',
            transform: `translateY(calc(-50% + ${(activeIndex - 1) * 30}px))`,
            transition: 'transform 1.2s cubic-bezier(0.16, 1, 0.3, 1)',
            willChange: 'transform',
          }}
        />

        {/* Big faded year */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none overflow-hidden">
          <span
            className="font-black leading-none"
            style={{ fontSize: 'clamp(180px, 32vw, 480px)', color: 'rgba(200,169,110,0.03)' }}
          >
            {60 + activeIndex}
          </span>
        </div>

        <div className="flex-1 flex items-center">
          <div className="max-w-7xl mx-auto px-10 w-full relative z-10">
            <div className="flex items-center gap-3 mb-10">
              <div className="w-10 h-px bg-[#c8a96e]" />
              <span className="text-[#c8a96e] text-xs font-black tracking-[0.3em] uppercase">The Journey</span>
            </div>

            <div className="grid grid-cols-2 gap-16 items-center">
              {/* LEFT */}
              <div className="relative">
                <div style={contentStyle}>
                  {/* Year badge — NO icon */}
                  <div className="mb-7">
                    <span className="text-[#c8a96e] text-xs font-black tracking-[0.25em] uppercase bg-[#c8a96e]/10 border border-[#c8a96e]/25 px-4 py-2 rounded-sm">
                      {active.year}
                    </span>
                  </div>
                  <h2 className="text-5xl lg:text-6xl font-black text-white leading-tight mb-6 tracking-tight">
                    {active.label}
                  </h2>
                  <p className="text-gray-300 text-lg leading-[1.85] mb-10 max-w-lg">{active.body}</p>
                  <div className="flex items-end gap-4 border-l-2 border-[#c8a96e] pl-5">
                    <span className="text-5xl font-black text-[#c8a96e] leading-none">{active.stat}</span>
                    <span className="text-gray-500 text-xs uppercase tracking-widest mb-1">{active.statLabel}</span>
                  </div>
                </div>

                <div className="flex items-center gap-3 mt-12">
                  {journey.map((_, i) => (
                    <div
                      key={i}
                      className="rounded-full transition-all duration-500"
                      style={{
                        width: i === activeIndex ? '28px' : '8px',
                        height: '8px',
                        background: i === activeIndex ? '#c8a96e' : i < activeIndex ? 'rgba(200,169,110,0.4)' : 'rgba(255,255,255,0.1)',
                        boxShadow: i === activeIndex ? '0 0 10px 3px rgba(200,169,110,0.5)' : 'none',
                      }}
                    />
                  ))}
                  <span className="text-gray-600 text-xs font-bold tracking-widest ml-2">
                    {activeIndex + 1} / {journey.length}
                  </span>
                </div>

                <div
                  className="flex items-center gap-3 mt-6"
                  style={{ opacity: showScrollHint ? 1 : 0, transition: 'opacity 0.6s ease', pointerEvents: 'none' }}
                >
                  <div style={{ animation: 'bounceHint 1.8s ease-in-out infinite' }} className="flex-shrink-0">
                    <i className="ri-arrow-down-line text-[#c8a96e]/50 text-base" />
                  </div>
                  <span className="text-gray-600 text-xs tracking-[0.2em] uppercase">Keep scrolling to follow the story</span>
                </div>
              </div>

              {/* RIGHT — step cards */}
              <div className="flex flex-col gap-3">
                {journey.map((step, i) => {
                  const isActive = i === activeIndex;
                  const isPast = i < activeIndex;
                  return (
                    <div
                      key={step.year}
                      className="relative flex items-center gap-5 px-6 py-5 rounded-sm border overflow-hidden"
                      style={{
                        borderColor: isActive ? 'rgba(200,169,110,0.5)' : isPast ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.03)',
                        background: isActive ? 'rgba(200,169,110,0.06)' : isPast ? 'rgba(255,255,255,0.02)' : 'transparent',
                        opacity: isActive ? 1 : isPast ? 0.5 : 0.18,
                        transform: isActive ? 'translateX(12px) scale(1.02)' : 'translateX(0) scale(1)',
                        boxShadow: isActive ? '0 0 30px rgba(200,169,110,0.12), inset 0 0 20px rgba(200,169,110,0.03)' : 'none',
                        transition: 'all 0.55s cubic-bezier(0.16, 1, 0.3, 1)',
                      }}
                    >
                      {isActive && (
                        <div
                          className="absolute left-0 top-0 bottom-0 w-[3px] rounded-r-full"
                          style={{ background: 'linear-gradient(to bottom, transparent, #c8a96e, transparent)', boxShadow: '0 0 10px 2px rgba(200,169,110,0.4)' }}
                        />
                      )}
                      <div className="relative flex-shrink-0 w-3 h-3 z-10">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{
                            background: isActive ? '#c8a96e' : isPast ? 'rgba(200,169,110,0.4)' : 'rgba(255,255,255,0.08)',
                            boxShadow: isActive ? '0 0 10px 3px rgba(200,169,110,0.6)' : 'none',
                            transition: 'all 0.4s ease',
                          }}
                        />
                        {isActive && (
                          <div className="absolute inset-0 rounded-full bg-[#c8a96e]" style={{ animation: 'pingGlow 1.6s ease-out infinite' }} />
                        )}
                      </div>
                      <div className="flex-1 min-w-0 z-10">
                        <div className="text-xs font-black tracking-[0.2em] uppercase mb-0.5" style={{ color: isActive ? '#c8a96e' : '#374151', transition: 'color 0.4s ease' }}>
                          {step.year}
                        </div>
                        <div className="text-sm font-bold" style={{ color: isActive ? '#ffffff' : '#374151', transition: 'color 0.4s ease' }}>
                          {step.label}
                        </div>
                      </div>
                      {isPast && (
                        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 z-10">
                          <i className="ri-check-line text-[#c8a96e]/40 text-sm" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom scroll indicator */}
        <div className="flex-shrink-0 flex flex-col items-center gap-2 pb-6 pt-2">
          <span className="text-gray-600 text-[10px] tracking-[0.3em] uppercase">Keep Scrolling</span>
          <div style={{ animation: 'bounceHint 1.8s ease-in-out infinite' }}>
            <div className="w-5 h-8 rounded-full border border-white/15 flex items-start justify-center pt-1.5">
              <div className="w-1 h-2 rounded-full bg-[#c8a96e]" style={{ animation: 'scrollDot 1.8s ease-in-out infinite' }} />
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes pingGlow {
          0%   { transform: scale(1); opacity: 0.7; }
          100% { transform: scale(3.2); opacity: 0; }
        }
        @keyframes bounceHint {
          0%, 100% { transform: translateY(0); }
          50%       { transform: translateY(5px); }
        }
        @keyframes scrollDot {
          0%   { transform: translateY(0);    opacity: 1; }
          75%  { transform: translateY(10px); opacity: 0; }
          100% { transform: translateY(0);    opacity: 0; }
        }
      `}</style>
    </section>
  );
}

// ─── Root export — picks the right version based on screen size ───────────────
export default function About() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler, { passive: true });
    return () => window.removeEventListener('resize', handler);
  }, []);

  return isMobile ? <MobileJourney /> : <DesktopJourney />;
}
